import { Fab } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import { Z_INDEX } from '@/constants/styleConstants';
import { classnames, isServer } from '@/helpers';
import { customTwMerge } from '@/helpers/theme';
import { ReactNode } from 'react';
import * as ReactModal from 'react-modal';
import { twMerge } from 'tailwind-merge';
import css from './Modal.module.scss';

const Header = ({
  title,
  onClose,
  onBack,
  newDesign,
  floating,
}: {
  title?: string;
  onClose?: (e) => void;
  onBack?: () => void;
  newDesign?: boolean;
  floating?: boolean;
}) => {
  if (newDesign) {
    return (
      <div className={`${floating ? 'rounded-t-lg' : ''} px-xs py-md bg-surface_l1 flex items-center`}>
        <div className="flex flex-1 justify-center">
          <h2 className="text-h-s text-fg_alwaysLight">{title}</h2>
        </div>
        {onClose && (
          <button onClick={onClose} className="right-sm absolute outline-none">
            <Icon color="fg_alwaysLight" variant="close-alt" style={{ height: '20px', width: '20px' }} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={classnames(css.header, 'gap-lg flex items-center')}>
      <div className="gap-lg flex flex-1 items-center">
        {onBack && (
          <Fab
            className="shrink-0 grow-0"
            onClick={onBack}
            icon={<Icon variant="arrow-left" />}
            floating={false}
            size="md"
          />
        )}
        <h2 className="text-lg font-bold">{title}</h2>
      </div>
      {onClose && <Fab onClick={onClose} icon={<Icon variant="close" />} size="md" floating={false} />}
    </div>
  );
};

const Footer = ({ atBottom = false, children }: { atBottom?: boolean; children?: ReactNode }) => {
  return (
    <div className={classnames(css.footer, atBottom && 'pb-lg mt-auto')}>
      <div className="gap-md flex justify-end">{children}</div>
    </div>
  );
};

const FLOATING_CONTENT_PADDING_PX = 18;

const Content = ({
  children,
  floating,
  style,
  wrapperStyle,
  contentPadding = true,
  gradientBackground,
  contentMaxWidth,
  classNames,
  newDesign,
}: {
  children: ReactNode;
  floating: boolean;
  style?: React.CSSProperties;
  wrapperStyle?: React.CSSProperties;
  contentPadding?: boolean;
  gradientBackground?: boolean;
  contentMaxWidth?: number;
  classNames?: string;
  newDesign?: boolean;
}) => {
  const wrapperDefaultStyle = {
    ...(contentMaxWidth && { maxWidth: contentMaxWidth }),
    ...(floating && { padding: wrapperStyle?.['padding'] ? wrapperStyle['padding'] : FLOATING_CONTENT_PADDING_PX }),
    ...(wrapperStyle ? wrapperStyle : {}),
  } as React.CSSProperties;

  if (newDesign) {
    return (
      <div
        className={classnames(
          css.modal,
          floating && css.floating,
          'outline-none',
          floating ? 'max-h-screen w-full max-w-[560px] flex-1 sm:p-0' : 'h-full w-full max-w-full',
        )}
        style={wrapperDefaultStyle}>
        <div
          style={style}
          className={classnames(
            'bg-surface_l4 relative z-10 flex flex-col opacity-100',
            floating ? 'rounded-lg' : `min-h-full`,
          )}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className={classnames(
        css.modal,
        floating && css.floating,
        'outline-none',
        floating ? 'max-h-screen w-full max-w-[400px] flex-1 sm:p-0' : 'h-full w-full max-w-full',
      )}
      style={wrapperDefaultStyle}>
      <div
        style={style}
        className={twMerge(
          classnames(
            'gap-lg relative z-10 flex flex-col bg-white opacity-100',
            floating && contentPadding ? 'p-lg my-lg' : `${gradientBackground ? 'bg-gradient' : ''} pt-4xl`,
            floating ? 'rounded-lg' : `min-h-full`,
          ),
          classNames,
        )}>
        {children}
      </div>
    </div>
  );
};

export type ModalProps = {
  isOpen: boolean;
  children?: ReactNode;
  className?: string;
  shouldCloseOnOverlayClick?: boolean;
  overlayClassName?: string;
  center?: boolean;
  onRequestClose?: (e) => void;
};

const Modal = ({
  isOpen,
  children,
  onRequestClose,
  shouldCloseOnOverlayClick = true,
  overlayClassName,
  className = '',
  center = true,
}: ModalProps) => {
  return (
    <ReactModal
      {...{
        isOpen,
        onRequestClose,
        shouldCloseOnOverlayClick,
        appElement: isServer ? null : document.getElementById('root'),
      }}
      overlayClassName={
        overlayClassName ?? `fixed flex justify-center inset-0 outline-none bg-overlay-50 max-w-full overflow-y-scroll`
      }
      className={customTwMerge(
        classnames('relative h-full w-full', center && 'flex items-center justify-center'),
        className,
      )}
      overlayElement={(props, contentElement) => {
        /**
         * we need to remove these classes from the overlay element since addblockers can block the modal
         * In this case the "shield" in Brave browser is blocking the modal because of these classes
         */
        const { className, ...rest } = props;
        const removeDefault = [
          'ReactModal__Overlay',
          'ReactModal__Overlay--after-open',
          'ReactModal__Overlay--before-close',
        ];
        const classes = className
          .split(' ')
          .filter((c) => !removeDefault.includes(c))
          .join(' ');

        return (
          <div {...rest} className={classes}>
            {contentElement}
          </div>
        );
      }}
      style={{ overlay: { zIndex: Z_INDEX.MODAL } }}>
      {children}
    </ReactModal>
  );
};

Modal.Header = Header;
Modal.Footer = Footer;
Modal.Content = Content;

export default Modal;
